import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { EmptyState, SkeletonRows } from '@/components/ui/States'
import { ReviewActions } from '@/components/review/ReviewActions'
import { PreviouslyRejectedFlag } from '@/components/review/VerdictHistory'
import { pathOf } from '@/lib/format'
import type { PartnerFact } from '@/types'

/**
 * PLACEHOLDER RENDERING — deliberately generic.
 *
 * The Standard Facts fixed template is not defined yet (docs/00-overview.md
 * §5.7). This renders a key/value list with inline edit against the generic
 * fact shape, and each row carries its own verdict and source link.
 *
 * When the template lands, replace the body of this component. It is a
 * rendering change, not a rebuild: the data shape, the review vocabulary, and
 * the mutations all stay as they are. Do not design the template
 * speculatively in here.
 */
export function StandardFactsList({
  facts,
  loading,
  onSave,
  onReview,
  onAdd,
}: {
  facts: PartnerFact[]
  loading: boolean
  onSave: (id: string, value: string) => void
  onReview: (id: string, status: 'approved' | 'rejected') => void
  onAdd: () => void
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  if (loading) return <SkeletonRows rows={4} cols={2} />

  if (facts.length === 0) {
    return (
      <EmptyState
        title="No standard facts yet"
        description="The fixed Standard Facts template is not defined. Until it is, add facts by hand and they render as a plain labelled list."
        action={
          <Button size="sm" onClick={onAdd}>
            Add fact
          </Button>
        }
      />
    )
  }

  return (
    <dl className="divide-y divide-rule">
      {facts.map((fact) => {
        const isEditing = editing === fact.id
        return (
          <div key={fact.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
            <dt className="w-[190px] shrink-0">
              <span className="field-label">{fact.label}</span>
              {fact.previouslyRejected && (
                <span className="mt-1 block">
                  <PreviouslyRejectedFlag compact />
                </span>
              )}
            </dt>

            <dd className="flex min-w-[240px] flex-1 items-center gap-2">
              {isEditing ? (
                <>
                  <Input
                    autoFocus
                    value={draft}
                    aria-label={`Value for ${fact.label}`}
                    className="h-8 text-[13px]"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onSave(fact.id, draft)
                        setEditing(null)
                      }
                      if (e.key === 'Escape') setEditing(null)
                    }}
                  />
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                      onSave(fact.id, draft)
                      setEditing(null)
                    }}
                  >
                    Save
                  </Button>
                  <Button size="sm" onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-[13px] leading-5">{fact.value || '—'}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDraft(fact.value)
                      setEditing(fact.id)
                    }}
                  >
                    Edit
                  </Button>
                </>
              )}
            </dd>

            <div className="flex shrink-0 items-center gap-2">
              {fact.sourceUrl && (
                <a
                  href={fact.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={fact.sourceUrl}
                  className="inline-flex items-center gap-1 font-mono text-[11px] text-ink-faint hover:text-brand-blue"
                >
                  {pathOf(fact.sourceUrl)}
                  <ExternalLink size={10} aria-hidden />
                </a>
              )}
              <ReviewActions
                size="sm"
                status={fact.status}
                onApprove={() => onReview(fact.id, 'approved')}
                onReject={() => onReview(fact.id, 'rejected')}
              />
            </div>
          </div>
        )
      })}
    </dl>
  )
}
